import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import PhoneIcon from '@mui/icons-material/Phone';
import Slider from '@mui/material/Slider';
import { usePayment } from '../../context/PaymentContext';
import { useAuth } from '../../context/AuthContext';

/**
 * ProfileBoostPayment
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - userId: string
 * - isEmployer: boolean (choose correct price)
 * - settings: system settings object (expects boost.employerBoostPrice, boost.jobSeekerBoostPrice, payments.currency)
 * - onPaid: (transaction) => void
 */
export default function ProfileBoostPayment({ open, onClose, userId, isEmployer, settings, onPaid }) {
  const { processProfileBoostPayment, commissionRates } = usePayment();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [durationDays, setDurationDays] = useState(() => Number(settings?.boost?.defaultDurationDays || 30));
  const [mobileOperator, setMobileOperator] = useState('airtel');
  const [mobileNumber, setMobileNumber] = useState('');

  const basePrice = useMemo(() => {
    const val = isEmployer ? settings?.boost?.employerBoostPrice : settings?.boost?.jobSeekerBoostPrice;
    return Number(val || 0);
  }, [settings, isEmployer]);
  const currency = useMemo(() => settings?.payments?.currency || 'USD', [settings]);
  const commissionPct = useMemo(() => Number(commissionRates?.premiumSubscription || 0), [commissionRates]);
  const baseTotal = useMemo(() => Number(durationDays || 1) * basePrice, [basePrice, durationDays]);
  const commission = useMemo(() => (baseTotal * commissionPct) / 100, [baseTotal, commissionPct]);
  const totalAmount = useMemo(() => baseTotal + commission, [baseTotal, commission]);
  const canSubmit = useMemo(() => {
    // Require mobile number selection and valid format when paying
    const numOk = /^(09|07)\d{8}$/.test(mobileNumber);
    return numOk && !!mobileOperator && !loading;
  }, [mobileNumber, mobileOperator, loading]);

  const handleConfirmPayment = async () => {
    try {
      setLoading(true);
      setError('');
      if (!currentUser) {
        setError('Please login to proceed with payment.');
        return;
      }
      const uid = userId || currentUser?._id || currentUser?.uid;
      const transaction = await processProfileBoostPayment(uid, baseTotal, durationDays, {
        paymentMethod: 'mobile',
        mobileOperator,
        mobileNumber,
      });
      if (transaction) {
        onPaid?.(transaction);
      }
    } catch (e) {
      console.error('Profile boost payment failed:', e);
      setError('Failed to process payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Confirm Profile Boost</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" gutterBottom>
          Boosting your profile highlights it in directories and featured sections for the configured duration.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography gutterBottom>Select duration (days)</Typography>
            <Slider
              value={durationDays}
              onChange={(_, v) => setDurationDays(Number(v))}
              valueLabelDisplay="auto"
              min={1}
              max={30}
              marks={[{ value: 1, label: '1' }, { value: 7, label: '7' }, { value: 14, label: '14' }, { value: 30, label: '30' }]}
            />
            <Typography variant="caption" color="text.secondary">Selected: {durationDays} day(s)</Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <FormLabel component="legend">Mobile Money Network</FormLabel>
            <RadioGroup
              row
              value={mobileOperator}
              onChange={(e) => setMobileOperator(e.target.value)}
            >
              <FormControlLabel value="airtel" control={<Radio />} label="Airtel Money" />
              <FormControlLabel value="mtn" control={<Radio />} label="MTN Mobile Money" />
              <FormControlLabel value="zamtel" control={<Radio />} label="Zamtel Kwacha" />
            </RadioGroup>
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Mobile Money Number"
              placeholder="0977123456"
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ''))}
              helperText="We’ll send a payment prompt to this number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                )
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Supported networks: Airtel, MTN, Zamtel. Use a Zambian number.
            </Typography>
          </Box>

          <List>
            <ListItem>
              <ListItemText primary={`Base Price per day (${currency})`} secondary={basePrice.toFixed(2)} />
            </ListItem>
            <ListItem>
              <ListItemText primary={`Base Total (${currency})`} secondary={baseTotal.toFixed(2)} />
            </ListItem>
            <ListItem>
              <ListItemText primary={`Platform Fee (${commissionPct}% )`} secondary={commission.toFixed(2)} />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Total"
                secondary={<Typography variant="h6">{currency} {totalAmount.toFixed(2)}</Typography>}
              />
            </ListItem>
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleConfirmPayment} disabled={!canSubmit}>
          {loading ? 'Processing…' : 'Confirm & Pay'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}